import type { MouseEvent as ReactMouseEvent } from 'react'
import {
  HANDLE_BORDER_WIDTH,
  MIN_CHROME_SIZE_PX,
  ROTATION_CENTER_COLOR,
  ROTATION_CENTER_SIZE,
  SELECTION_DASH_ARRAY,
} from '@/components/app/editor/selection-chrome/selection-chrome-constants'
import { GEOMETRY_BLOCKED_PULSE_CLASS } from '@/components/app/editor/selection-chrome/geometry-blocked-feedback'
import { chromeGeometry } from '@/lib/shapes/selection-chrome-preview'

import { ChromeEdgeStrips } from './components/chrome-edge-strips'
import { ChromeMemberOutlines } from './components/chrome-member-outlines'
import { ChromeResizeDots } from './components/chrome-resize-dots'
import { ChromeRotationKnob } from './components/chrome-rotation-knob'
import { createChromeGestureHandlers } from './handlers/chrome-gesture-handlers'
import {
  resolveChromeBodyCursor,
  resolveChromeStrokeColor,
  resolveDecorationOpacity,
} from './helpers/chrome-appearance'
import { formatPolygonPoints } from './helpers/chrome-points'
import { resolveSelectionChromeFrame } from './helpers/selection-chrome-frame'
import { useGeometryBlockedPulse } from './hooks/use-geometry-blocked-pulse'
import type { SelectionChromeLayerProps } from './types/selection-chrome-layer-props'

/**
 * Selection chrome, drawn on the visual layer in screen pixels.
 *
 * Replaces the `SelectionChrome` instances that `ShapeManager` renders inside the zoom-scaled canvas.
 * Two things change, and nothing else is meant to:
 *
 * 1. **Sizes are literal.** The frame stroke is 1px, handles are `HANDLE_SIZE`, the rotation stem is
 *    `ROTATE_HANDLE_CENTER_OFFSET` — no `--chrome-inv-scale` counter-transform,
 *    no `vector-effect="non-scaling-stroke"`, no device-pixel-grid snapping. The same constants are
 *    reused so the chrome looks identical; they just aren't pre-divided by zoom any more.
 * 2. **The frame follows the rotated *visual* box**, so it hugs an inset shape (triangle, star)
 *    instead of standing off its edges.
 *
 * Gesture math is untouched: every interaction is forwarded straight to `ShapeManager`, which
 * continues to compute moves, resizes and rotations against the **geometric** rect. This component
 * decides where chrome is drawn, never what a drag means.
 */
export function SelectionChromeLayer({
  shapeManagerRef,
  selected,
  multiSelectChrome,
  selectionState,
  scale,
  interactive = true,
}: SelectionChromeLayerProps) {
  const showBlockedPulse =
    useGeometryBlockedPulse(selectionState?.geometryBlockedPulseKey ?? 0) && interactive
  const isMultiSelection = selected.length > 1
  const frame = resolveSelectionChromeFrame(selected, multiSelectChrome)

  if (!frame || selected.length === 0) return null

  const locked = selectionState?.locked ?? false
  const frozenOnly = selectionState?.frozenOnly ?? false
  const geometryInactive = frozenOnly
  // Gestures need BOTH an interactive chrome and an unlocked, unfrozen selection.
  const gesturesEnabled = interactive && !locked && !frozenOnly

  const strokeColor = resolveChromeStrokeColor(locked, frozenOnly)

  const geometry = chromeGeometry(frame, {
    scale,
    minScreenPx: MIN_CHROME_SIZE_PX,
    devicePixelSnap: true,
  })
  const framePoints = formatPolygonPoints(geometry.corners)

  const bodyCursor = resolveChromeBodyCursor(locked, frozenOnly)

  const { beginMove, beginResize, beginRotate } = createChromeGestureHandlers({
    shapeManagerRef,
    gesturesEnabled,
    pivot: geometry.pivot,
  })

  return (
    <g
      data-selection-chrome-layer=""
      /* Right-click routing lives on the GROUP, not on the interior move polygon, so every
         hit-testable child opens the object context menu. Only that polygon used to carry it, but
         the edge strips, handle hit rects and rotation hit circle below all render for a non-locked
         selection and paint AFTER it, so they win hit-testing wherever they overlap. With no
         handler of their own nothing called stopPropagation() and the event escaped the chrome to
         the pasteboard, which opened the canvas BACKGROUND menu instead — reported on frozen
         objects (WMPCM-9825), but true of any non-locked selection: locked was only immune because
         it renders nothing but the polygon. Handled here so a new child cannot reopen the hole. */
      onContextMenu={
        interactive
          ? (event: ReactMouseEvent<SVGElement>) =>
              shapeManagerRef?.current?.handleChromeContextMenu?.(
                event as unknown as ReactMouseEvent<HTMLDivElement>
              )
          : undefined
      }
    >
      {isMultiSelection && (
        <ChromeMemberOutlines selected={selected} scale={scale} strokeColor={strokeColor} />
      )}

      {/* Frame interior stays grabbable, matching the old chrome container: dragging anywhere inside
          the selection box moves the selection, including over empty parts of an inset shape.
          `pointerEvents: all` makes an unfilled polygon hit-test its interior. */}
      <polygon
        points={framePoints}
        fill="none"
        stroke={strokeColor}
        strokeWidth={HANDLE_BORDER_WIDTH}
        strokeDasharray={isMultiSelection && !locked ? SELECTION_DASH_ARRAY : undefined}
        style={
          interactive ? { pointerEvents: 'all', cursor: bodyCursor } : { pointerEvents: 'none' }
        }
        onPointerDown={interactive ? beginMove : undefined}
        onDoubleClick={
          interactive
            ? (event: ReactMouseEvent<SVGElement>) =>
                shapeManagerRef?.current?.handleChromeDoubleClick?.(
                  event as unknown as ReactMouseEvent<HTMLDivElement>
                )
            : undefined
        }
      />

      {showBlockedPulse && (
        <polygon
          key={`pulse-${selectionState?.geometryBlockedPulseKey ?? 0}`}
          points={framePoints}
          fill="none"
          stroke="#dc2626"
          strokeWidth={HANDLE_BORDER_WIDTH}
          className={GEOMETRY_BLOCKED_PULSE_CLASS}
          style={{ pointerEvents: 'none' }}
        />
      )}

      {/* Skipped entirely when non-interactive: an edge strip is invisible and exists only to start
          a resize, so a peer highlight would gain a transparent band that swallows canvas clicks. */}
      {!locked && interactive && (
        <ChromeEdgeStrips
          geometry={geometry}
          geometryInactive={geometryInactive}
          beginResize={beginResize}
        />
      )}

      {!locked && (
        <ChromeRotationKnob
          geometry={geometry}
          strokeColor={strokeColor}
          geometryInactive={geometryInactive}
          interactive={interactive}
          beginRotate={beginRotate}
        />
      )}

      {!locked && (
        <ChromeResizeDots
          geometry={geometry}
          strokeColor={strokeColor}
          geometryInactive={geometryInactive}
          interactive={interactive}
          beginResize={beginResize}
        />
      )}

      {!locked && (
        <circle
          cx={geometry.pivot.x}
          cy={geometry.pivot.y}
          r={ROTATION_CENTER_SIZE / 2}
          fill={ROTATION_CENTER_COLOR}
          opacity={resolveDecorationOpacity(geometryInactive)}
          style={{ pointerEvents: 'none' }}
        />
      )}
    </g>
  )
}

export default SelectionChromeLayer
